import { GetActiveEntryPointParams, StickyNavApiEntry } from '@interfaces/models/sticky-nav';
import { WOMEN_UNIVERSE } from '@constants/user-shopping-preferences';
import { isHomePage } from '@helpers/routing';
import { getOrigin } from '@helpers/utils/mmao';
import { STICKY_NAV_PARAMS, appendStickyNavParams } from '@helpers/utils/sticky-nav/sticky-nav-utils';

const getStickyCategoryIdFromQuery = (pathWithQuery: string): string | null => {
  try {
    const url = new URL(pathWithQuery, getOrigin());
    return url.searchParams.get(STICKY_NAV_PARAMS.STICKY_CATEGORY_ID);
  } catch {
    return null;
  }
};

export const isUniverseEntryPoint = (entryPoint: StickyNavApiEntry): boolean =>
  (!entryPoint.destination?.categoryId || entryPoint.destination.categoryId === '') &&
  (entryPoint.mnemonic === 'men' || entryPoint.mnemonic === 'women');

const getEntryPointForHomepage = (entryPoints: StickyNavApiEntry[], currentUniverse: number): string | null => {
  const womenEntry = entryPoints.find((entry) => entry.mnemonic === 'women');
  const menEntry = entryPoints.find((entry) => entry.mnemonic === 'men');

  if (womenEntry && !menEntry) {
    return womenEntry.id;
  }
  if (menEntry && !womenEntry) {
    return menEntry.id;
  }

  const preferredEntry = currentUniverse === WOMEN_UNIVERSE ? womenEntry : menEntry;
  return preferredEntry?.id ?? null;
};

const getEntryPointByStickyCategoryId = (entryPoints: StickyNavApiEntry[], routerAsPath: string): string | null => {
  const categoryId = getStickyCategoryIdFromQuery(routerAsPath);
  if (!categoryId) {
    return null;
  }

  const match = entryPoints.find((entry) => entry.destination?.categoryId === categoryId);
  return match?.id ?? null;
};

export const getActiveEntryPoint = ({
  routerPathname,
  routerAsPath,
  entryPoints,
  currentUniverse,
}: GetActiveEntryPointParams): string | null => {
  if (isHomePage(routerPathname, routerAsPath)) {
    return getEntryPointForHomepage(entryPoints, currentUniverse);
  }

  return getEntryPointByStickyCategoryId(entryPoints, routerAsPath);
};

export const getEffectiveAsPath = (currentUrl: string, fallbackAsPath: string): string => {
  if (!currentUrl) {
    return fallbackAsPath;
  }

  try {
    const url = new URL(currentUrl, getOrigin());
    return url.pathname + url.search;
  } catch {
    return fallbackAsPath;
  }
};

export const getStickyEntryPoints = (
  stickyNavData: StickyNavApiEntry[] | null | undefined,
  currentUniverse?: number,
): StickyNavApiEntry[] => {
  return (stickyNavData ?? [])
    .filter((entry) => entry.destination?.universalLink)
    .map((entry) => {
      const categoryId = entry.destination?.categoryId || '';

      if (!entry.destination?.universalLink) {
        return { ...entry, destination: { ...entry.destination, categoryId } };
      }

      if (entry.destination.universalLink === '/') {
        const params = new URLSearchParams();
        appendStickyNavParams(params, { universe: currentUniverse });
        const queryString = params.toString();
        const transformedLink = queryString ? `/?${queryString}` : '/';
        return {
          ...entry,
          destination: { ...entry.destination, universalLink: transformedLink, categoryId },
        };
      }

      try {
        const baseOrigin = getOrigin();
        if (!baseOrigin) {
          return { ...entry, destination: { ...entry.destination, categoryId } };
        }

        const baseUrl = `${baseOrigin}${entry.destination.universalLink}`;
        const url = new URL(baseUrl);
        appendStickyNavParams(url.searchParams, {
          categoryId: categoryId || undefined,
          universe: currentUniverse,
        });

        const transformedLink = url.pathname + url.search + url.hash;

        return {
          ...entry,
          destination: { ...entry.destination, universalLink: transformedLink, categoryId },
        };
      } catch {
        return { ...entry, destination: { ...entry.destination, categoryId } };
      }
    });
};
